import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Building2, Plus, FileText, Eye, EyeOff, Shield, UserCheck } from 'lucide-react';
import ProjectCard from '../components/ProjectCard';
import CreateProjectModal from '../components/CreateProjectModal';
import { getStatusLabel, getPriorityLabel } from '../types';
import type { Ticket } from '../types';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

export default function OrganizationPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [showCreateProject, setShowCreateProject] = useState(false);

  const { data: orgData, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.getOrganization(id!),
    enabled: !!id,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { organization_id: id }],
    queryFn: () => api.getProjects({ organization_id: id }),
    enabled: !!id,
  });

  const { data: directTicketsData } = useQuery({
    queryKey: ['org-direct-tickets', id],
    queryFn: () => api.getOrganizationTickets(id!, { direct_only: 'true', limit: 20 }),
    enabled: !!id,
  });

  const org = orgData?.data;
  const projects = projectsData?.data ?? [];
  const directTickets = directTicketsData?.data ?? [];
  const isAdmin = user?.role === 'admin';

  const billingLabels: Record<string, string> = {
    hourly: t('organization.hourly'),
    retainer: t('organization.hourBank'),
    hour_bank: t('organization.hourBank'),
    fixed_price: t('organization.fixedPrice'),
  };

  if (orgLoading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  if (!org) return <div className="card text-center py-12 text-surface-500">{t('organization.notFound')}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <Building2 size={24} className="text-primary-600" />
          </div>
          <div>
            <h1 className="page-title">{org.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-surface-500">{billingLabels[org.billing_model] || org.billing_model}</span>
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-surface-100 text-surface-600">
                {org.ticket_visibility === 'all' ? (
                  <><Eye size={10} /> {t('organization.allTickets')}</>
                ) : (
                  <><EyeOff size={10} /> {t('organization.ownTickets')}</>
                )}
              </span>
              {org.hourly_rate > 0 && (
                <span className="text-xs text-surface-400">₪{org.hourly_rate}/{t('common.hoursShort')}</span>
              )}
              {org.account_manager_name && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
                  <UserCheck size={10} /> {t('admin.accountManager')}: {org.account_manager_name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Projects section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-surface-800">{t('organization.projects')}</h2>
          {isAdmin && (
            <button onClick={() => setShowCreateProject(true)}
              className="btn btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> {t('organization.newProject')}
            </button>
          )}
        </div>

        {projects.length === 0 ? (
          <div className="card text-center py-8 text-surface-400 text-sm">{t('organization.noProjects')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                id={p.id}
                name={p.name}
                type={p.type}
                status={p.status}
                ticketCount={p.ticket_count}
                openTicketCount={p.open_ticket_count}
                totalActualHours={parseFloat(String(p.total_actual_hours)) || 0}
                totalBudgetHours={p.total_budget_hours ? parseFloat(String(p.total_budget_hours)) : undefined}
                overdueTicketCount={(p as any).overdue_ticket_count}
                endDate={p.end_date}
              />
            ))}
          </div>
        )}
      </section>

      {/* Direct tickets section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-surface-800">{t('organization.directTickets')}</h2>
          <span className="text-xs text-surface-400">{t('organization.noProject')}</span>
        </div>

        {directTickets.length === 0 ? (
          <div className="card text-center py-6 text-surface-400 text-sm">{t('organization.noDirectTickets')}</div>
        ) : (
          <div className="card overflow-hidden">
            <div className="divide-y divide-surface-100">
              {directTickets.map((ticket: Ticket) => (
                <Link key={ticket.id} to={`/tickets/${ticket.id}`}
                  className="flex items-center justify-between p-3 hover:bg-surface-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={16} className="text-surface-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-surface-800 truncate">{ticket.title}</p>
                      <p className="text-xs text-surface-400">
                        {ticket.agent_name || t('common.unassigned')} · {format(new Date(ticket.created_at), 'dd/MM/yyyy', { locale: he })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      ticket.priority === 'urgent' ? 'bg-danger-100 text-danger-700' :
                      ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      'bg-surface-100 text-surface-600'
                    }`}>
                      {getPriorityLabel(ticket.priority, t)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      ticket.status === 'open' ? 'bg-info-100 text-info-700' :
                      ticket.status === 'in_progress' ? 'bg-warning-100 text-warning-700' :
                      ticket.status === 'waiting' ? 'bg-orange-100 text-orange-700' :
                      'bg-success-100 text-success-700'
                    }`}>
                      {getStatusLabel(ticket.status, t)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* SLA Settings — admin only */}
      {isAdmin && (
        <SlaSettingsSection orgId={id!} org={org} />
      )}

      {showCreateProject && (
        <CreateProjectModal organizationId={id!} onClose={() => setShowCreateProject(false)} />
      )}
    </div>
  );
}

function SlaSettingsSection({ orgId, org }: { orgId: string; org: any }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [responseHours, setResponseHours] = useState(String(org.sla_response_hours ?? 1));
  const [resolutionHours, setResolutionHours] = useState(String(org.sla_resolution_hours ?? 48));
  const [bhStart, setBhStart] = useState(org.business_hours_start ?? '09:00');
  const [bhEnd, setBhEnd] = useState(org.business_hours_end ?? '18:00');
  const [days, setDays] = useState<number[]>(org.business_days ?? [0, 1, 2, 3, 4]);

  const dayLabels = [
    t('sla.sun'), t('sla.mon'), t('sla.tue'), t('sla.wed'),
    t('sla.thu'), t('sla.fri'), t('sla.sat'),
  ];

  const mutation = useMutation({
    mutationFn: () =>
      api.updateOrganizationSla(orgId, {
        sla_response_hours: parseFloat(responseHours),
        sla_resolution_hours: parseFloat(resolutionHours),
        business_hours_start: bhStart,
        business_hours_end: bhEnd,
        business_days: days,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
      setEditing(false);
    },
  });

  const toggleDay = (d: number) => {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-surface-800 flex items-center gap-2">
          <Shield size={18} className="text-primary-600" />
          {t('sla.slaSettings')}
        </h2>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn btn-secondary text-sm">
            {t('common.edit')}
          </button>
        )}
      </div>

      {!editing ? (
        <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-surface-500">{t('sla.responseTime')}</span>
            <p className="font-medium text-surface-900">{org.sla_response_hours ?? 1}h</p>
          </div>
          <div>
            <span className="text-surface-500">{t('sla.resolutionTime')}</span>
            <p className="font-medium text-surface-900">{org.sla_resolution_hours ?? 48}h</p>
          </div>
          <div>
            <span className="text-surface-500">{t('sla.businessHours')}</span>
            <p className="font-medium text-surface-900">{org.business_hours_start ?? '09:00'} – {org.business_hours_end ?? '18:00'}</p>
          </div>
          <div className="sm:col-span-2 md:col-span-3">
            <span className="text-surface-500">{t('sla.businessDays')}</span>
            <div className="flex gap-2 mt-1">
              {dayLabels.map((label, i) => (
                <span key={i} className={`px-2 py-1 rounded text-xs ${
                  (org.business_days ?? [0, 1, 2, 3, 4]).includes(i)
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-surface-100 text-surface-400'
                }`}>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('sla.responseTime')}</label>
              <input type="number" step="0.5" min="0.5" className="input" value={responseHours}
                onChange={(e) => setResponseHours(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('sla.resolutionTime')}</label>
              <input type="number" step="1" min="1" className="input" value={resolutionHours}
                onChange={(e) => setResolutionHours(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('sla.businessHours')} — {t('common.start')}</label>
              <input type="time" className="input" value={bhStart}
                onChange={(e) => setBhStart(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('sla.businessHours')} — {t('common.end')}</label>
              <input type="time" className="input" value={bhEnd}
                onChange={(e) => setBhEnd(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">{t('sla.businessDays')}</label>
            <div className="flex gap-2 flex-wrap">
              {dayLabels.map((label, i) => (
                <button key={i} type="button"
                  onClick={() => toggleDay(i)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    days.includes(i)
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface-100 text-surface-500 hover:bg-surface-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {mutation.isError && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-sm">
              {t('common.saveError')}
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
              className="btn btn-primary text-sm">
              {mutation.isPending ? t('common.saving') : t('common.save')}
            </button>
            <button onClick={() => setEditing(false)} className="btn bg-surface-100 text-surface-700 text-sm">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
